"use client"

import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, Trash2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import axios from "axios"

export function IntegrationFormModal({
  open,
  onOpenChange,
  mode = "create",
  integration = null,
  canEdit = false,
  canDelete = false,
  onSuccess,
}) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  })

  const isEdit = mode === "edit"

  useEffect(() => {
    if (open) {
      if (isEdit && integration) {
        setFormData({
          name: integration.name || "",
          description: integration.description || "",
        })
      } else {
        setFormData({ name: "", description: "" })
      }
    }
  }, [open, isEdit, integration])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canEdit) return

    if (!formData.name.trim()) {
      toast({
        title: "Validation error",
        description: "Integration name is required.",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    try {
      if (isEdit && integration?.id) {
        await axios.put(`/integrations/${integration.id}`, {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
        })
        toast({
          title: "Success",
          description: "Integration updated successfully.",
        })
      } else {
        await axios.post("/integrations", {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          category: "crm",
          type: "mapping",
          is_active: true,
        })
        toast({
          title: "Success",
          description: "Integration created successfully.",
        })
      }

      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      console.error("Integration save error:", error)
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          (isEdit ? "Failed to update integration." : "Failed to create integration."),
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!canDelete || !integration?.id) return

    setIsDeleting(true)

    try {
      await axios.delete(`/integrations/${integration.id}`)
      toast({
        title: "Deleted",
        description: "Integration removed successfully.",
      })
      setShowDeleteConfirm(false)
      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      console.error("Integration delete error:", error)
      toast({
        title: "Error",
        description:
          error.response?.data?.error || "Failed to delete integration.",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? "Edit Mapping Integration" : "Add Mapping Integration"}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update this integration used for external user ID mapping. It does not connect to an external API."
                : "Create a label for an external system so you can map user IDs to it (e.g. a CRM or internal tool)."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="integration-name">Name</Label>
              <Input
                id="integration-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="e.g. LeadPerfection, Salesforce"
                disabled={!canEdit || isSubmitting}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="integration-description">Description</Label>
              <Textarea
                id="integration-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Optional description for admins"
                disabled={!canEdit || isSubmitting}
                rows={3}
              />
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              {isEdit && canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 sm:mr-auto"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isSubmitting || isDeleting}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {canEdit && (
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="dark:text-white"
                >
                  {isSubmitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEdit ? "Save Changes" : "Create Integration"}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete integration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove &quot;{integration?.name}&quot;. You
              cannot delete integrations that have external user mappings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
